import styled from 'styled-components';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Footer } from 'components/Footer';
import { StoreList } from 'components/StoreList';
import { LocationBar } from 'components/LocationBar';
import { GoogleMaps } from './components/GoogleMaps';
import { ToggleViewBar } from 'components/ToggleViewBar';
import { breakpoints, colors, maxAppWidth } from './constants';
import { useStateContext } from 'context';
import { useTranslation } from 'react-i18next';

const Header = styled.div``;
const LogoContainer = styled.div``;
const LogoLinkWrapper = styled.a``;
const Logo = styled.img``;
const AppContainer = styled.div``;
const Grid = styled.div``;
const LocationColumn = styled.div``;
const MapColumn = styled.div``;
const ImageContainer = styled.div``;

interface IProps {
    className?: string;
}

const BaseApp = ({ className }: IProps) => {
    const [showToggle, setShowToggle] = useState(false);
    const {
        availableStores,
        mobileView,
        showMobileMap,
        searchLocation,
        isOakley,
        isNuance,
    } = useStateContext();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (showToggle) return;

        if (searchLocation) {
            setShowToggle(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchLocation]);

    useEffect(() => {
        document.title = isOakley
            ? t('seoOakley.title')
            : isNuance
            ? t('seoNuance.title')
            : t('seoRayban.title');

        const metaDesc = document.querySelector("meta[name='description']");

        const metaDescText = isOakley
            ? t('seoOakley.descriptionContent')
            : isNuance
            ? t('seoNuance.descriptionContent')
            : t('seoRayban.descriptionContent');

        if (metaDesc) {
            metaDesc.setAttribute('content', metaDescText);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const hideStoreList = mobileView && showMobileMap;
    const hideMap = mobileView && !showMobileMap;

    const imageUrl = isOakley
        ? 'https://grandvision.s3.amazonaws.com/pblx/images/oakley-meta-banner.jpg'
        : isNuance
        ? 'https://grandvision.s3.amazonaws.com/pblx/images/nuance-audio-banner.jpg'
        : 'https://grandvision.s3.amazonaws.com/pblx/images/pnl-ray-ban-meta-banner.jpg';
    const pearleUrl =
        i18n.language === 'fr'
            ? 'https://www.pearle.be/fr_BE'
            : 'https://www.pearle.be/';


    return (
        <div className={className}>
            <AppContainer>
                <Header>
                    <LogoContainer>
                        <LogoLinkWrapper href={pearleUrl}>
                            <Logo src="https://grandvision.s3.amazonaws.com/pblx/images/pearle-logo-transparent.svg" />
                        </LogoLinkWrapper>
                    </LogoContainer>
                </Header>
                <Grid>
                    <LocationColumn className={clsx({ 'span-2': hideMap })}>
                        <ImageContainer>
                            <img src={imageUrl} alt="Meta banner" />
                        </ImageContainer>
                        <LocationBar />
                        {mobileView && showToggle && <ToggleViewBar />}
                        {availableStores && (
                            <StoreList
                                availableStores={availableStores}
                                className={clsx({
                                    'display-none': hideStoreList,
                                })}
                            />
                        )}
                    </LocationColumn>
                    <MapColumn
                        className={clsx({
                            'span-2': hideStoreList,
                            'display-none': hideMap,
                        })}
                    >
                        <GoogleMaps />
                    </MapColumn>
                </Grid>
            </AppContainer>
            <Footer />
        </div>
    );
};

export const App = styled(BaseApp)`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;

    ${ImageContainer} {
        padding-bottom: 2.5rem;
    }

    img {
        max-width: 100%;
        height: auto;
    }

    ${AppContainer} {
        flex: 1 0 auto;
    }

    ${Header} {
        position: fixed;
        background-color: #fff;
        border-bottom: 1px solid ${colors.greyLight};
        z-index: 1;
        width: 100%;
        height: 62px;

        @media (${breakpoints.mediumMin}) {
            height: 74px;
        }
    }

    ${LogoContainer} {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        max-width: ${maxAppWidth + 'px'};
        margin: 0 auto;
        padding: 0 16px;
    }

    ${LogoLinkWrapper} {
        background-color: ${colors.green};
        height: 100%;
        padding: 16px 22px;

        @media (${breakpoints.mediumMin}) {
            padding: 21px 14px;
        }
    }

    ${Logo} {
        height: 100%;
    }

    ${Grid} {
        display: grid;
        grid-template-columns: 100% 0;
        height: 100%;
        max-width: ${maxAppWidth + 'px'};
        margin: 0 auto;

        @media (${breakpoints.smallMin}) {
            grid-template-columns: 40% auto;
            grid-gap: 1rem;
        }

        @media (${breakpoints.mediumMin}) {
            grid-template-columns: 37.5% auto;
        }
    }

    ${MapColumn} {
        height: 100vh;

        @media (${breakpoints.smallMin}) {
            position: sticky;
            margin: 0;
            top: 0;
        }
    }

    .display-none {
        display: none;
    }

    .span-2 {
        grid-column-start: span 2;
    }

    ${LocationColumn} {
        padding: 100px 1rem 0;
    }

    @media (${breakpoints.smallMin}) {
        ${MapColumn} {
            padding: 100px 0 1rem;
        }
    }
`;
